import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Task,
  Tag,
  User,
  ProjectTemplateFullDto,
  ProjectTemplateTaskItemDto,
  TemplateTaskDataDto,
  TemplateChildTaskDto,
} from "../types/types.ts";
import {
  PaletteColor,
  PaletteColorHex,
  PaletteColorDarkHex,
} from "../types/PaletteColor.ts";
import { useDarkMode } from "../hooks/useDarkMode.ts";
import { MemberSelectionDialog } from "./MemberSelectionDialog.tsx";

// Material-UI アイコン
import {
  Close as CloseIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import PeopleTwoToneIcon from "@mui/icons-material/PeopleTwoTone";
import EventTwoToneIcon from "@mui/icons-material/EventTwoTone";
import LabelImportantTwoToneIcon from "@mui/icons-material/LabelImportantTwoTone";
import StyleTwoToneIcon from "@mui/icons-material/StyleTwoTone";
import NoteAltTwoToneIcon from "@mui/icons-material/NoteAltTwoTone";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AssignmentTurnedInTwoToneIcon from "@mui/icons-material/AssignmentTurnedInTwoTone";
import SubdirectoryArrowRightTwoToneIcon from "@mui/icons-material/SubdirectoryArrowRightTwoTone";
import PersonAddTwoToneIcon from "@mui/icons-material/PersonAddTwoTone";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import Switch from "@mui/material/Switch";

interface Props {
  projectTemplateId: number;
  onClose: () => void;
  roomMembers: User[];
  availableTags: Tag[];
  color: PaletteColor;
  honbuId: number;
  roomId: number;
  onUpdated?: () => void;
}

// 選択タスクのキー型
type TaskKey = { taskIndex: number; subIndex?: number };

export function ProjectTemplateEditorPanel({
  projectTemplateId,
  onClose,
  roomMembers,
  availableTags,
  color,
  honbuId,
  roomId,
  onUpdated,
}: Props) {
  const isDarkMode = useDarkMode();
  const isMyList = roomId === 0;

  // --- 状態管理 ---
  const [fullData, setFullData] = useState<ProjectTemplateFullDto | null>(null);
  const [selectedKey, setSelectedKey] = useState<TaskKey>({ taskIndex: 0 });
  const [view, setView] = useState<"list" | "detail">("list");
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [memberDialog, setMemberDialog] = useState<{
    type: "assignees" | "requesters";
  } | null>(null);
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);
  const [dateRangeFlags, setDateRangeFlags] = useState<Record<string, boolean>>(
    {},
  );

  // --- データ取得 ---
  useEffect(() => {
    const fetchFullData = async () => {
      try {
        const res = await fetch(
          `/api/project-templates/${projectTemplateId}/full?honbuId=${honbuId}`,
        );
        if (res.ok) {
          const data = await res.json();
          setFullData(data);

          // 日付範囲フラグの初期化
          const flags: Record<string, boolean> = {};
          data.tasks.forEach(
            (task: ProjectTemplateTaskItemDto, taskIdx: number) => {
              const taskData = JSON.parse(task.taskData) as TemplateTaskDataDto;
              flags[`${taskIdx}`] = !!(
                taskData.startDateOffset != null &&
                taskData.endDateOffset != null &&
                taskData.startDateOffset !== taskData.endDateOffset
              );

              (taskData.childTasks || []).forEach((_, subIdx) => {
                flags[`${taskIdx}-${subIdx}`] = !!(
                  taskData.childTasks[subIdx].startDateOffset != null &&
                  taskData.childTasks[subIdx].endDateOffset != null &&
                  taskData.childTasks[subIdx].startDateOffset !==
                    taskData.childTasks[subIdx].endDateOffset
                );
              });
            },
          );
          setDateRangeFlags(flags);
        }
      } catch (e) {
        console.error("Failed to fetch project template full data", e);
      }
    };
    fetchFullData();
  }, [projectTemplateId, honbuId]);

  // --- 保存ロジック (デバウンス) ---
  const saveTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isDirty || !fullData) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(async () => {
      setIsSaving(true);
      try {
        const res = await fetch(
          `/api/project-templates/${projectTemplateId}/full?honbuId=${honbuId}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(fullData),
          },
        );
        if (res.ok) {
          setIsDirty(false);
          setShowSaveConfirmation(true);
          setTimeout(() => setShowSaveConfirmation(false), 2000);
          onUpdated?.();
        }
      } catch (e) {
        console.error("Auto save failed", e);
      } finally {
        setIsSaving(false);
      }
    }, 1000);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [fullData, isDirty]);

  // --- 現在選択中のタスクデータ取得 ---
  const selectedTaskData = useMemo(():
    | TemplateTaskDataDto
    | TemplateChildTaskDto
    | null => {
    if (!fullData || !fullData.tasks[selectedKey.taskIndex]) return null;
    const taskData = JSON.parse(
      fullData.tasks[selectedKey.taskIndex].taskData,
    ) as TemplateTaskDataDto;

    if (selectedKey.subIndex !== undefined) {
      return taskData.childTasks[selectedKey.subIndex] || null;
    }
    return taskData;
  }, [fullData, selectedKey]);

  // 現在のタスクキー文字列
  const currentKeyString =
    selectedKey.subIndex !== undefined
      ? `${selectedKey.taskIndex}-${selectedKey.subIndex}`
      : `${selectedKey.taskIndex}`;

  const currentIsDateRange = dateRangeFlags[currentKeyString] || false;

  // --- 更新処理 ---
  const updateCurrentTaskData = (
    updates: Partial<TemplateTaskDataDto> | Partial<TemplateChildTaskDto>,
  ) => {
    if (!fullData || !selectedTaskData) return;

    const newTasks = [...fullData.tasks];
    const taskData = JSON.parse(
      newTasks[selectedKey.taskIndex].taskData,
    ) as TemplateTaskDataDto;

    if (selectedKey.subIndex !== undefined) {
      // サブタスクの更新
      taskData.childTasks[selectedKey.subIndex] = {
        ...taskData.childTasks[selectedKey.subIndex],
        ...updates,
      };
    } else {
      // 親タスクの更新
      Object.assign(taskData, updates);
    }

    newTasks[selectedKey.taskIndex] = {
      ...newTasks[selectedKey.taskIndex],
      taskData: JSON.stringify(taskData),
    };

    setFullData({ ...fullData, tasks: newTasks });
    setIsDirty(true);
  };

  // オフセット変換ヘルパー
  const offsetToIso = (offset: number | null | undefined): string => {
    if (offset == null) return "";
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + offset);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0",
    )}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const isoToOffset = (iso: string): number | null => {
    if (!iso) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(iso + "T00:00:00");
    return Math.round((target.getTime() - today.getTime()) / 86400000);
  };

  const getOffsetDisplay = (
    offset: number | null | undefined,
  ): string | number => {
    return offset === null || offset === undefined ? "" : offset;
  };

  const handleOffsetDirect = (type: "start" | "end", val: string) => {
    const n = parseInt(val, 10);
    if (isNaN(n)) return;

    const startOffset =
      type === "start" ? n : (selectedTaskData as any).startDateOffset;
    const endOffset =
      type === "end" ? n : (selectedTaskData as any).endDateOffset;

    if (!currentIsDateRange) {
      updateCurrentTaskData({ startDateOffset: n, endDateOffset: n });
    } else {
      if (type === "start") {
        updateCurrentTaskData({
          startDateOffset: n,
          endDateOffset: endOffset != null && n > endOffset ? n : endOffset,
        });
      } else {
        updateCurrentTaskData({
          startDateOffset:
            startOffset != null && n < startOffset ? n : startOffset,
          endDateOffset: n,
        });
      }
    }
  };

  // 新規タスク追加
  const handleAddNewRootTask = () => {
    if (!fullData) return;
    const newTaskData: TemplateTaskDataDto = {
      title: "新規タスク",
      priority: "なし",
      notification: false,
      assigneeHonbuIds: [],
      requesterHonbuIds: [],
      tagIds: [],
      startDateOffset: 0,
      endDateOffset: 0,
      childTasks: [],
    };
    const newTask: ProjectTemplateTaskItemDto = {
      id: 0,
      taskData: JSON.stringify(newTaskData),
      sortOrder: fullData.tasks.length,
    };
    setFullData({ ...fullData, tasks: [...fullData.tasks, newTask] });
    setSelectedKey({ taskIndex: fullData.tasks.length });
    setView("detail");
    setDateRangeFlags((f) => ({ ...f, [`${fullData.tasks.length}`]: false }));
    setIsDirty(true);
  };

  // タスク削除
  const handleDeleteTask = () => {
    if (!fullData) return;

    if (selectedKey.subIndex !== undefined) {
      // サブタスク削除
      const taskData = JSON.parse(
        fullData.tasks[selectedKey.taskIndex].taskData,
      ) as TemplateTaskDataDto;
      const newChildTasks = taskData.childTasks.filter(
        (_, i) => i !== selectedKey.subIndex,
      );
      updateCurrentTaskData({ childTasks: newChildTasks } as any);
      setSelectedKey({ taskIndex: selectedKey.taskIndex });
    } else {
      // 親タスク削除
      if (fullData.tasks.length <= 1) {
        alert("プロジェクトには少なくとも1つのタスクが必要です。");
        return;
      }
      if (!window.confirm("このタスクをテンプレートから削除しますか？")) return;

      const newTasks = fullData.tasks.filter(
        (_, i) => i !== selectedKey.taskIndex,
      );
      setFullData({ ...fullData, tasks: newTasks });
      setSelectedKey({ taskIndex: 0 });
      setIsDirty(true);
    }
    setOpenMenuKey(null);
  };

  // サブタスク追加
  const handleAddSubtask = () => {
    if (selectedKey.subIndex !== undefined) return; // サブタスク選択中は追加不可

    const taskData = JSON.parse(
      fullData!.tasks[selectedKey.taskIndex].taskData,
    ) as TemplateTaskDataDto;
    const newSub: TemplateChildTaskDto = {
      title: "新規サブタスク",
      assigneeHonbuIds: [],
      requesterHonbuIds: [],
      tagIds: [],
      startDateOffset: taskData.startDateOffset,
      endDateOffset: taskData.endDateOffset,
      childTasks: [],
    };
    const newChildTasks = [...taskData.childTasks, newSub];
    updateCurrentTaskData({ childTasks: newChildTasks } as any);

    const newSubIndex = newChildTasks.length - 1;
    setSelectedKey({ taskIndex: selectedKey.taskIndex, subIndex: newSubIndex });
    setView("detail");
    setDateRangeFlags((f) => ({
      ...f,
      [`${selectedKey.taskIndex}-${newSubIndex}`]: false,
    }));
    setOpenMenuKey(null);
  };

  // メンバー選択の保存
  const handleMemberSave = (selectedNames: string[]) => {
    if (!memberDialog) return;
    const selectedIds = selectedNames
      .map((name) => Number(roomMembers.find((m) => m.name === name)?.id))
      .filter((id) => !isNaN(id));

    if (memberDialog.type === "assignees") {
      updateCurrentTaskData({ assigneeHonbuIds: selectedIds });
    } else {
      updateCurrentTaskData({ requesterHonbuIds: selectedIds });
    }
    setMemberDialog(null);
  };

  // タグ変更
  const handleTagChange = (tag: Tag) => {
    if (!selectedTaskData || selectedKey.subIndex !== undefined) return;
    const currentTagIds =
      (selectedTaskData as TemplateTaskDataDto).tagIds || [];
    const newTagIds = currentTagIds.includes(tag.id)
      ? currentTagIds.filter((id) => id !== tag.id)
      : [...currentTagIds, tag.id];
    updateCurrentTaskData({ tagIds: newTagIds });
  };

  const switchStyles = {
    "&.Mui-checked": {
      color: PaletteColorHex[color],
      "& + .MuiSwitch-track": { backgroundColor: PaletteColorHex[color] },
    },
  };

  if (!fullData || !selectedTaskData) return null;

  const isParentTask = selectedKey.subIndex === undefined;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[70] flex justify-end"
      onClick={onClose}
    >
      <div
        className={`w-full md:w-[768px] h-full shadow-2xl flex flex-col animate-slide-in-right ${
          isDarkMode ? "bg-gray-900 text-gray-100" : "bg-white text-gray-800"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div className="p-4 border-b flex justify-between items-center bg-gray-50/50">
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={fullData.templateName}
              onChange={(e) => {
                setFullData({ ...fullData, templateName: e.target.value });
                setIsDirty(true);
              }}
              className="font-bold text-lg bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 outline-none transition-all"
            />
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* 左側: タスクリスト（デスクトップでは常に表示、モバイルではlist時のみ） */}
          <div
            className={`md:w-1/3 border-r flex flex-col bg-gray-50/30 ${
              view === "list" ? "w-full" : "hidden md:flex"
            }`}
          >
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              <div className="px-2 py-1 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                タスク構成
              </div>
              {fullData.tasks.map((t, taskIdx) => {
                const taskData = JSON.parse(t.taskData) as TemplateTaskDataDto;
                const isSelected =
                  selectedKey.taskIndex === taskIdx &&
                  selectedKey.subIndex === undefined;
                const menuKey = `${taskIdx}`;

                return (
                  <div key={taskIdx} className="space-y-1">
                    {/* 親タスク */}
                    <div
                      onClick={() => {
                        setSelectedKey({ taskIndex: taskIdx });
                        setView("detail");
                      }}
                      className={`group py-1 px-3 rounded-lg cursor-pointer flex items-center justify-between transition-all ${
                        isSelected
                          ? "text-white shadow-md ring-2"
                          : "hover:bg-gray-100"
                      }`}
                      style={
                        isSelected
                          ? {
                              backgroundColor: PaletteColorHex[color],
                              boxShadow: `0 0 0 2px ${PaletteColorHex[color]}40`,
                            }
                          : undefined
                      }
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <AssignmentTurnedInTwoToneIcon
                          fontSize="small"
                          className={
                            isSelected ? "text-white" : "text-gray-400"
                          }
                        />
                        <span className="text-sm font-medium truncate">
                          {taskData.title || "(無題)"}
                        </span>
                      </div>
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuKey(
                              openMenuKey === menuKey ? null : menuKey,
                            );
                          }}
                          className={`opacity-0 group-hover:opacity-100 p-1 rounded-full hover:bg-black/10 transition-all ${
                            isSelected ? "text-white" : "text-gray-400"
                          }`}
                        >
                          <MoreVertIcon style={{ fontSize: 16 }} />
                        </button>
                        {openMenuKey === menuKey && (
                          <>
                            <div
                              className="fixed inset-0 z-40"
                              onClick={() => setOpenMenuKey(null)}
                            />
                            <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-50 py-1 border border-gray-200">
                              <button
                                onClick={() => {
                                  setSelectedKey({ taskIndex: taskIdx });
                                  handleAddSubtask();
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                サブタスクを追加
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedKey({ taskIndex: taskIdx });
                                  handleDeleteTask();
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                              >
                                削除
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* サブタスク */}
                    {taskData.childTasks.map((sub, subIdx) => {
                      const isSubSelected =
                        selectedKey.taskIndex === taskIdx &&
                        selectedKey.subIndex === subIdx;
                      const subMenuKey = `${taskIdx}-${subIdx}`;

                      return (
                        <div
                          key={subIdx}
                          onClick={() => {
                            setSelectedKey({
                              taskIndex: taskIdx,
                              subIndex: subIdx,
                            });
                            setView("detail");
                          }}
                          className={`group p-2 pl-6 rounded-lg cursor-pointer text-sm flex items-center justify-between transition-all ${
                            isSubSelected
                              ? "text-white shadow-md ring-2"
                              : "hover:bg-gray-100"
                          }`}
                          style={
                            isSubSelected
                              ? {
                                  backgroundColor: PaletteColorHex[color],
                                  boxShadow: `0 0 0 2px ${PaletteColorHex[color]}40`,
                                }
                              : undefined
                          }
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <SubdirectoryArrowRightTwoToneIcon
                              fontSize="small"
                              className={
                                isSubSelected ? "text-white" : "text-gray-400"
                              }
                            />
                            <span className="truncate">
                              {sub.title || "新規サブタスク"}
                            </span>
                          </div>
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuKey(
                                  openMenuKey === subMenuKey
                                    ? null
                                    : subMenuKey,
                                );
                              }}
                              className={`opacity-0 group-hover:opacity-100 p-1 rounded-full hover:bg-black/10 transition-all ${
                                isSubSelected ? "text-white" : "text-gray-400"
                              }`}
                            >
                              <MoreVertIcon style={{ fontSize: 14 }} />
                            </button>
                            {openMenuKey === subMenuKey && (
                              <>
                                <div
                                  className="fixed inset-0 z-40"
                                  onClick={() => setOpenMenuKey(null)}
                                />
                                <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-50 py-1 border border-gray-200">
                                  <button
                                    onClick={() => {
                                      setSelectedKey({
                                        taskIndex: taskIdx,
                                        subIndex: subIdx,
                                      });
                                      handleDeleteTask();
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                  >
                                    削除
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            <div className="p-3 border-t">
              <button
                onClick={handleAddNewRootTask}
                className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 transition-all text-sm font-bold flex items-center justify-center gap-1 hover:bg-opacity-10"
                style={
                  {
                    "--hover-color": PaletteColorHex[color],
                  } as React.CSSProperties
                }
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = PaletteColorHex[color];
                  e.currentTarget.style.color = PaletteColorHex[color];
                  e.currentTarget.style.backgroundColor = `${PaletteColorHex[color]}10`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "";
                  e.currentTarget.style.color = "";
                  e.currentTarget.style.backgroundColor = "";
                }}
              >
                <AddIcon fontSize="small" />
                タスクを追加
              </button>
            </div>
          </div>

          {/* 右側: 詳細エディタ（デスクトップでは常に表示、モバイルではdetail時のみ） */}
          <div
            className={`flex-1 flex flex-col min-h-0 ${
              view === "detail" ? "w-full" : "hidden md:flex"
            }`}
          >
            {/* モバイル用: 戻るボタン */}
            {view === "detail" && (
              <div className="md:hidden flex items-center p-3 border-b">
                <button
                  onClick={() => setView("list")}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100"
                >
                  <ArrowBackIcon fontSize="small" />
                  タスク一覧に戻る
                </button>
              </div>
            )}
            <div className="space-y-4 overflow-y-auto flex-1 p-6 pr-4">
              {/* タイトル */}
              <input
                type="text"
                value={selectedTaskData.title || ""}
                onChange={(e) =>
                  updateCurrentTaskData({ title: e.target.value })
                }
                className="text-xl font-bold text-gray-800 w-full border-b-2 focus:border-blue-500 outline-none"
                placeholder="タイトルを入力..."
              />

              {/* 依頼者セクション - マイリストでは非表示 */}
              {!isMyList && (
                <div className="flex items-center space-x-2 min-w-0">
                  <span className="text-xs font-bold text-gray-500 flex-shrink-0">
                    依頼者：
                  </span>
                  <span className="text-xs truncate min-w-0">
                    {(selectedTaskData.requesterHonbuIds || [])
                      .map((id) => {
                        const name = roomMembers.find(
                          (m) => Number(m.id) === id,
                        )?.name;
                        return name || `ID:${id}`;
                      })
                      .join(", ") || "ー"}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setMemberDialog({ type: "requesters" });
                    }}
                    className="text-gray-500 hover:text-blue-500 py-1 -m-1"
                    title="依頼者を編集"
                  >
                    <PersonAddAlt1OutlinedIcon fontSize="small" />
                  </button>
                </div>
              )}

              {/* 担当者 */}
              {!isMyList && (
                <div>
                  <div className="flex items-center text-gray-500 gap-2">
                    <PeopleTwoToneIcon fontSize="small" />
                    <span className="text-sm font-bold">
                      担当者 ({selectedTaskData.assigneeHonbuIds?.length || 0}
                      名)
                    </span>
                  </div>
                  <div
                    className="flex items-center flex-wrap gap-1 mt-2 p-2 border rounded min-h-[40px] cursor-pointer"
                    onClick={() => setMemberDialog({ type: "assignees" })}
                  >
                    {(selectedTaskData.assigneeHonbuIds || []).map((id) => {
                      const name =
                        roomMembers.find((m) => Number(m.id) === id)?.name ||
                        `ID:${id}`;
                      return (
                        <span
                          key={id}
                          className="text-xs px-2 py-1 bg-gray-200 rounded-full"
                        >
                          {name}
                        </span>
                      );
                    })}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setMemberDialog({ type: "assignees" });
                      }}
                      className="text-sm hover:underline ml-auto"
                      style={{ color: PaletteColorHex[color] }}
                    >
                      <PersonAddTwoToneIcon
                        fontSize="small"
                        className="mb-1 mr-1"
                      />
                      追加
                    </button>
                  </div>
                </div>
              )}

              {/* 期限 */}
              <div>
                <div className="flex items-center text-gray-500 gap-2">
                  <EventTwoToneIcon fontSize="small" />
                  <span className="text-sm font-bold">
                    {currentIsDateRange
                      ? "期間 (作成日からの日数)"
                      : "期限 (作成日からの日数)"}
                  </span>
                </div>
                <div className="mt-2">
                  {selectedTaskData.startDateOffset == null && (
                    <div
                      className="flex items-center bg-gray-100 border rounded-md px-3 py-2 mb-2 shadow-sm cursor-pointer"
                      onClick={() => {
                        setDateRangeFlags((f) => ({
                          ...f,
                          [currentKeyString]: false,
                        }));
                        updateCurrentTaskData({
                          startDateOffset: 0,
                          endDateOffset: 0,
                        });
                      }}
                    >
                      未設定
                    </div>
                  )}

                  {selectedTaskData.startDateOffset != null && (
                    <div className="ml-1 mb-2">
                      {!currentIsDateRange && (
                        <div className="flex items-center bg-white border rounded-md px-3 py-2 shadow-sm">
                          <span className="text-sm text-gray-700 mr-2">
                            作成日の
                          </span>
                          <input
                            type="number"
                            min="0"
                            className="w-16 text-center p-1 border rounded focus:outline-none focus:border-blue-500"
                            value={getOffsetDisplay(
                              selectedTaskData.startDateOffset,
                            )}
                            onChange={(e) =>
                              handleOffsetDirect("start", e.target.value)
                            }
                            onFocus={(e) => e.target.select()}
                          />
                          <span className="text-sm text-gray-700 ml-2">
                            日後
                          </span>
                        </div>
                      )}
                      {currentIsDateRange && (
                        <div className="flex items-center bg-white border rounded-md px-3 py-2 shadow-sm gap-2 flex-wrap">
                          <span className="text-sm text-gray-700">
                            作成日の
                          </span>
                          <input
                            type="number"
                            min="0"
                            className="w-16 text-center p-1 border rounded focus:outline-none focus:border-blue-500"
                            value={getOffsetDisplay(
                              selectedTaskData.startDateOffset,
                            )}
                            onChange={(e) =>
                              handleOffsetDirect("start", e.target.value)
                            }
                            onFocus={(e) => e.target.select()}
                          />
                          <span className="text-sm text-gray-700">～</span>
                          <input
                            type="number"
                            min="0"
                            className="w-16 text-center p-1 border rounded focus:outline-none focus:border-blue-500"
                            value={getOffsetDisplay(
                              selectedTaskData.endDateOffset,
                            )}
                            onChange={(e) =>
                              handleOffsetDirect("end", e.target.value)
                            }
                            onFocus={(e) => e.target.select()}
                          />
                          <span className="text-sm text-gray-700">日後</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex items-center gap-4 mb-3">
                    <div className="flex items-center gap-1">
                      <Switch
                        checked={selectedTaskData.startDateOffset == null}
                        onChange={(_, checked) => {
                          if (checked) {
                            setDateRangeFlags((f) => ({
                              ...f,
                              [currentKeyString]: false,
                            }));
                            updateCurrentTaskData({
                              startDateOffset: null,
                              endDateOffset: null,
                            });
                          } else {
                            setDateRangeFlags((f) => ({
                              ...f,
                              [currentKeyString]: false,
                            }));
                            updateCurrentTaskData({
                              startDateOffset: 0,
                              endDateOffset: 0,
                            });
                          }
                        }}
                        size="small"
                        sx={switchStyles}
                      />
                      <span className="text-sm select-none">未設定にする</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Switch
                        checked={currentIsDateRange}
                        disabled={selectedTaskData.startDateOffset == null}
                        onChange={(_, checked) => {
                          setDateRangeFlags((f) => ({
                            ...f,
                            [currentKeyString]: checked,
                          }));
                          if (!checked) {
                            updateCurrentTaskData({
                              endDateOffset: selectedTaskData.startDateOffset,
                            });
                          } else if (
                            selectedTaskData.endDateOffset ===
                            selectedTaskData.startDateOffset
                          ) {
                            const startOffset =
                              selectedTaskData.startDateOffset || 0;
                            updateCurrentTaskData({
                              endDateOffset: startOffset + 1,
                            });
                          }
                        }}
                        size="small"
                        sx={switchStyles}
                      />
                      <span className="text-sm select-none">期間を設定</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 重要度（親タスクのみ） */}
              {isParentTask && (
                <div>
                  <div className="flex items-center text-gray-500 gap-2">
                    <LabelImportantTwoToneIcon fontSize="small" />
                    <span className="text-sm font-bold">重要度</span>
                  </div>
                  <div className="grid grid-cols-4 gap-1 mt-1 rounded-lg bg-gray-200 p-1">
                    {(["なし", "高", "中", "低"] as const).map((priority) => (
                      <button
                        key={priority}
                        type="button"
                        onClick={() =>
                          updateCurrentTaskData({ priority } as any)
                        }
                        className={`rounded-md py-1 text-sm font-semibold transition-colors duration-200 ${
                          (selectedTaskData as TemplateTaskDataDto).priority ===
                          priority
                            ? "bg-white text-gray-800 shadow"
                            : "bg-transparent text-gray-500 hover:bg-white/60"
                        }`}
                      >
                        {priority}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* タグ（親タスクのみ） */}
              {isParentTask && (
                <div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-gray-500 gap-2">
                      <StyleTwoToneIcon fontSize="small" />
                      <span className="text-sm font-bold">タグ</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {availableTags.map((tag) => {
                      const isSelected = (
                        (selectedTaskData as TemplateTaskDataDto).tagIds || []
                      ).includes(tag.id);
                      const hexColor = tag.color || PaletteColorHex.Blue;
                      return (
                        <div
                          key={tag.id}
                          onClick={() => handleTagChange(tag)}
                          style={{
                            cursor: "pointer",
                            backgroundColor: isSelected
                              ? `${hexColor}20`
                              : "white",
                            color: isSelected ? hexColor : `${hexColor}80`,
                            border: `1px solid ${hexColor}80`,
                          }}
                          className="px-3 py-1 rounded-full text-xs font-semibold"
                        >
                          #{tag.name}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* メモ */}
              <div>
                <div className="flex items-center text-gray-500 gap-2">
                  <NoteAltTwoToneIcon fontSize="small" />
                  <span className="text-sm font-bold">メモ</span>
                </div>
                <textarea
                  placeholder="メモを入力..."
                  className="mt-1 w-full min-h-[120px] border-2 border-gray-200 rounded-lg p-2"
                  value={selectedTaskData.memo || ""}
                  onChange={(e) =>
                    updateCurrentTaskData({ memo: e.target.value })
                  }
                />
              </div>
            </div>

            {/* フッター */}
            <div className="mt-auto pt-4 p-6 border-t h-8 flex items-center justify-center gap-2">
              {isSaving && (
                <div className="flex items-center justify-center text-gray-500">
                  <span>保存中...</span>
                </div>
              )}
              {!isSaving && showSaveConfirmation && (
                <div className="flex items-center justify-center text-green-600 animate-fade-in-out">
                  <CheckCircleIcon className="w-4 h-4 mr-1" />
                  <span>保存しました</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* メンバー選択ダイアログ */}
      {memberDialog && (
        <div className="relative z-[80]">
          <MemberSelectionDialog
            roomMembers={roomMembers}
            initialSelected={
              memberDialog.type === "assignees"
                ? (selectedTaskData.assigneeHonbuIds || []).map(
                    (id) =>
                      roomMembers.find((m) => Number(m.id) === id)?.name || "",
                  )
                : (selectedTaskData.requesterHonbuIds || []).map(
                    (id) =>
                      roomMembers.find((m) => Number(m.id) === id)?.name || "",
                  )
            }
            onSave={handleMemberSave}
            onClose={() => setMemberDialog(null)}
            dialogType={memberDialog.type}
          />
        </div>
      )}
    </div>
  );
}
